import { SITE, type JsonLdSchema } from './config';

export function organizationLd(): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE.url,
    logo: `${SITE.url}/parking-icon.svg`,
    description: SITE.description,
    sameAs: [
      'https://twitter.com/parksy',
      'https://www.youtube.com/@parksy',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'hi@parksy.rent',
      availableLanguage: ['English', 'Hindi', 'Tamil', 'Telugu', 'Bengali'],
    },
  };
}

export function webSiteLd(): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE.url}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function breadcrumbLd(items: BreadcrumbItem[]): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function faqLd(faqs: FaqItem[]): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };
}

export interface CityLdInput {
  slug: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
  pinCount?: number;
  avgMonthlyPrice?: number | null;
}

export function cityLd(input: CityLdInput): JsonLdSchema {
  const ld: JsonLdSchema = {
    '@context': 'https://schema.org',
    '@type': 'City',
    name: input.name,
    containedInPlace: {
      '@type': 'State',
      name: input.state,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: input.lat,
      longitude: input.lng,
    },
    url: `${SITE.url}/${input.slug}`,
  };

  if (typeof input.pinCount === 'number') {
    ld.additionalProperty = {
      '@type': 'PropertyValue',
      name: 'Parking spots listed',
      value: input.pinCount,
    };
  }

  if (typeof input.avgMonthlyPrice === 'number' && input.avgMonthlyPrice > 0) {
    ld.additionalProperty = [
      ld.additionalProperty,
      {
        '@type': 'PropertyValue',
        name: 'Average monthly parking price (INR)',
        value: Math.round(input.avgMonthlyPrice),
      },
    ].filter(Boolean);
  }

  return ld;
}

export interface PlaceLdInput {
  name: string;
  description: string;
  citySlug: string;
  localitySlug: string;
  lat: number;
  lng: number;
}

export function placeLd(input: PlaceLdInput): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: input.name,
    description: input.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: input.name,
      addressRegion: input.citySlug,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: input.lat,
      longitude: input.lng,
    },
    url: `${SITE.url}/${input.citySlug}/${input.localitySlug}`,
  };
}

export interface ArticleLdInput {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished: string;
  dateModified: string;
  authorName?: string;
}

export function articleLd(input: ArticleLdInput): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.headline,
    description: input.description,
    url: input.url,
    image: input.image ?? SITE.ogImage,
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: {
      '@type': 'Organization',
      name: input.authorName ?? 'The Parksy team',
      url: SITE.url,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE.url}/parking-icon.svg`,
      },
    },
    inLanguage: 'en-IN',
  };
}

export interface DatasetLdInput {
  name: string;
  description: string;
  url: string;
  citySlug: string;
  localitySlug?: string;
  rowCount?: number;
  updatedAt: string;
}

const DATASET_DESCRIPTION_MIN = 50;
const DATASET_DESCRIPTION_MAX = 500;
const DATASET_DESCRIPTION_PAD =
  ' Updated daily from crowdsourced pins contributed by users across India.';

function sanitizeDatasetDescription(raw: string): string {
  const normalized = raw.replace(/\s+/g, ' ').trim();
  if (
    normalized.length >= DATASET_DESCRIPTION_MIN &&
    normalized.length <= DATASET_DESCRIPTION_MAX
  ) {
    return normalized;
  }
  if (normalized.length > DATASET_DESCRIPTION_MAX) {
    return normalized.slice(0, DATASET_DESCRIPTION_MAX - 1).trimEnd() + '…';
  }
  const padded = normalized + DATASET_DESCRIPTION_PAD;
  return padded.length <= DATASET_DESCRIPTION_MAX
    ? padded
    : padded.slice(0, DATASET_DESCRIPTION_MAX - 1).trimEnd() + '…';
}

export function datasetLd(input: DatasetLdInput): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: input.name,
    description: sanitizeDatasetDescription(input.description),
    url: input.url,
    spatialCoverage: {
      '@type': 'Place',
      name: input.localitySlug
        ? `${input.localitySlug}, ${input.citySlug}, India`
        : `${input.citySlug}, India`,
    },
    temporalCoverage: input.updatedAt,
    ...(typeof input.rowCount === 'number' && {
      size: { '@type': 'PropertyValue', name: 'data points', value: input.rowCount },
    }),
    creator: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    license: 'https://creativecommons.org/licenses/by/4.0/',
  };
}

export function webPageLd(input: {
  name: string;
  description: string;
  url: string;
}): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: input.name,
    description: input.description,
    url: input.url,
    isPartOf: { '@type': 'WebSite', name: SITE.name, url: SITE.url },
    inLanguage: 'en-IN',
  };
}

export function softwareApplicationLd(): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: SITE.name,
    url: SITE.url,
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Any (browser)',
    description: SITE.description,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
  };
}

export interface VideoLdInput {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration: string;
  embedUrl: string;
  contentUrl?: string;
}

export function videoLd(input: VideoLdInput): JsonLdSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnailUrl,
    uploadDate: input.uploadDate,
    duration: input.duration,
    embedUrl: input.embedUrl,
    contentUrl: input.contentUrl ?? input.embedUrl,
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      logo: { '@type': 'ImageObject', url: `${SITE.url}/parking-icon.svg` },
    },
    inLanguage: 'en-IN',
    isAccessibleForFree: true,
  };
}
